(() => {
  if (customElements.get('image-carousel')) return;

  class ImageCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      if (!this.track || !this.slides.length) return;

      this.previous = this.querySelector('[data-previous]');
      this.next = this.querySelector('[data-next]');
      this.controls = this.querySelector('[data-controls]');
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.playToggle = this.querySelector('[data-play-toggle]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = this.dataset.autoplay === 'true';
      this.interval = Math.max(3000, Number(this.dataset.interval) || 3000);
      this.currentIndex = 0;
      this.targetIndex = 0;
      this.points = [];
      this.slidePoints = [];
      this.userPaused = false;
      this.hovered = false;
      this.focused = this.contains(document.activeElement);
      this.dragging = false;
      this.inView = false;
      this.resumeRequested = false;
      this.moving = false;
      this.editorSelections = new Set();
      this.abortController = new AbortController();
      const on = (target, event, callback, options = {}) => {
        target.addEventListener(event, callback, { ...options, signal: this.abortController.signal });
      };

      on(this.previous, 'click', () => this.step(-1));
      on(this.next, 'click', () => this.step(1));
      this.dots.forEach((dot, index) => on(dot, 'click', () => this.goTo(index)));
      on(this.playToggle, 'click', () => {
        this.userPaused = !this.userPaused;
        // An explicit Play action can resume while this control has focus.
        this.resumeRequested = !this.userPaused;
        this.updatePlayback();
        this.scheduleAutoplay();
      });
      on(this, 'pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.resumeRequested = false;
        this.scheduleAutoplay();
      });
      on(this, 'pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = false;
        this.scheduleAutoplay();
      });
      on(this, 'focusin', () => {
        this.focused = true;
        this.resumeRequested = false;
        this.scheduleAutoplay();
      });
      on(this, 'focusout', (event) => {
        this.focused = this.contains(event.relatedTarget);
        this.scheduleAutoplay();
      });
      on(this.track, 'pointerdown', () => {
        this.dragging = true;
        this.moving = false;
        this.resumeRequested = false;
        this.stopAutoplay();
      }, { passive: true });
      const release = () => {
        if (!this.dragging) return;
        this.dragging = false;
        this.settleScroll();
      };
      on(document, 'pointerup', release, { passive: true });
      on(document, 'pointercancel', release, { passive: true });
      on(this.track, 'wheel', () => {
        this.moving = false;
        this.resumeRequested = false;
        this.stopAutoplay();
        this.settleScroll();
      }, { passive: true });
      on(this.track, 'scroll', () => {
        this.stopAutoplay();
        if (!this.moving) this.setActive(this.nearestPoint());
        this.settleScroll();
      }, { passive: true });
      on(this.track, 'keydown', (event) => {
        let index;
        const current = this.moving ? this.targetIndex : this.currentIndex;
        if (event.key === 'ArrowRight') index = current + (this.rtl ? -1 : 1);
        if (event.key === 'ArrowLeft') index = current + (this.rtl ? 1 : -1);
        if (event.key === 'Home') index = 0;
        if (event.key === 'End') index = this.points.length - 1;
        if (index === undefined || event.altKey || event.ctrlKey || event.metaKey) return;
        event.preventDefault();
        this.goTo(index);
      });
      on(document, 'visibilitychange', () => this.scheduleAutoplay());
      on(this.motion, 'change', () => {
        this.resumeRequested = false;
        this.updatePlayback();
        this.scheduleAutoplay();
      });

      // Shopify replaces section elements in the editor; all listeners and
      // observers belong to this instance and are removed on disconnect.
      const section = this.closest('.shopify-section');
      on(document, 'shopify:section:select', (event) => {
        if (event.target !== section) return;
        this.editorSelections.add('section');
        this.stopAutoplay();
      });
      on(document, 'shopify:section:deselect', (event) => {
        if (event.target !== section) return;
        this.editorSelections.delete('section');
        this.scheduleAutoplay();
      });
      on(document, 'shopify:block:select', (event) => {
        const slide = event.target.closest('[data-slide]');
        const index = this.slides.indexOf(slide);
        if (index === -1) return;
        this.editorSelections.add('block');
        this.goTo(this.slidePoints[index]);
      });
      on(document, 'shopify:block:deselect', (event) => {
        if (!this.contains(event.target)) return;
        this.editorSelections.delete('block');
        this.scheduleAutoplay();
      });

      this.resizeObserver = new ResizeObserver(() => {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.measure());
      });
      this.resizeObserver.observe(this.track);
      this.intersectionObserver = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.15;
        this.scheduleAutoplay();
      }, { threshold: [0, 0.15] });
      this.intersectionObserver.observe(this.track);
      this.measure();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      clearTimeout(this.scrollTimer);
      cancelAnimationFrame(this.resizeFrame);
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
    }

    measure() {
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.rtl = getComputedStyle(this.track).direction === 'rtl';
      const first = this.slides[0].getBoundingClientRect();
      this.points = [];
      this.slidePoints = this.slides.map((slide) => {
        const rect = slide.getBoundingClientRect();
        const offset = this.rtl ? first.right - rect.right : rect.left - first.left;
        const position = Math.max(0, Math.min(offset, maximum));
        // The last few slide starts can clamp to the same end position.
        if (!this.points.length || position - this.points[this.points.length - 1] > 1) {
          this.points.push(position);
        }
        return this.points.length - 1;
      });
      const canSlide = maximum > 1 && this.points.length > 1;
      this.setControlHidden(this.previous, !canSlide);
      this.setControlHidden(this.next, !canSlide);
      this.setControlHidden(this.controls, !canSlide);
      this.dots.forEach((dot, index) => {
        this.setControlHidden(dot, index >= this.points.length);
        dot.setAttribute('aria-label', `${this.dataset.labelSlide} ${index + 1} ${this.dataset.labelOf} ${this.points.length}`);
      });
      this.moving = false;
      this.targetIndex = Math.min(this.currentIndex, this.points.length - 1);
      this.track.scrollTo({ left: (this.rtl ? -1 : 1) * this.points[this.targetIndex], behavior: 'instant' });
      this.setActive(this.targetIndex);
      this.updatePlayback();
      this.scheduleAutoplay();
    }

    nearestPoint() {
      const position = Math.abs(this.track.scrollLeft);
      return this.points.reduce((nearest, point, index) => (
        Math.abs(point - position) < Math.abs(this.points[nearest] - position) ? index : nearest
      ), 0);
    }

    setActive(index) {
      this.currentIndex = index;
      this.dots.forEach((dot, dotIndex) => {
        if (dotIndex === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    step(direction) {
      this.goTo((this.moving ? this.targetIndex : this.currentIndex) + direction);
    }

    goTo(index) {
      if (this.points.length < 2) return;
      this.targetIndex = (index + this.points.length) % this.points.length;
      this.moving = true;
      this.stopAutoplay();
      this.setActive(this.targetIndex);
      this.track.scrollTo({
        left: (this.rtl ? -1 : 1) * this.points[this.targetIndex],
        behavior: this.motion.matches ? 'instant' : 'smooth',
      });
      this.settleScroll();
    }

    settleScroll() {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        this.moving = false;
        this.setActive(this.nearestPoint());
        this.scheduleAutoplay();
      }, 180);
    }

    updatePlayback() {
      this.setControlHidden(this.playToggle, !this.autoplay || this.motion.matches || this.points.length < 2);
      this.playToggle.setAttribute('aria-label', this.userPaused ? this.dataset.labelPlay : this.dataset.labelPause);
      this.playToggle.querySelector('[data-icon-play]').hidden = !this.userPaused;
      this.playToggle.querySelector('[data-icon-pause]').hidden = this.userPaused;
    }

    setControlHidden(control, hidden) {
      // Keep keyboard focus in the carousel when responsive controls disappear.
      if (hidden && control.contains(document.activeElement)) this.track.focus({ preventScroll: true });
      control.hidden = hidden;
    }

    stopAutoplay() {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    scheduleAutoplay() {
      this.stopAutoplay();
      if (!this.isConnected || !this.autoplay || this.userPaused || this.motion.matches ||
          !this.inView || document.hidden || this.dragging || this.moving ||
          this.editorSelections.size || this.points.length < 2 ||
          (!this.resumeRequested && (this.hovered || this.focused))) return;
      this.autoplayTimer = setTimeout(() => this.step(1), this.interval);
    }
  }

  customElements.define('image-carousel', ImageCarousel);
})();
